import type { MapFailure } from './config';

/** MapLibre 6.14 tile_manager._tileLoaded emits a tile/coord data event
 * without sourceDataType. Metadata/content events alone are not tile evidence.
 */
export function isLoadedProviderTile(event: {
  sourceId?: string;
  source?: { type?: string };
  tile?: unknown;
}): boolean {
  const tile = event.tile;
  return (
    typeof event.sourceId === 'string' &&
    !event.sourceId.startsWith('vs09-') &&
    (event.source?.type === 'raster' || event.source?.type === 'vector') &&
    typeof tile === 'object' &&
    tile !== null &&
    'state' in tile &&
    tile.state === 'loaded'
  );
}

export type MapLoadState = 'MAP_LOADING' | 'MAP_BASEMAP_REAL' | MapFailure;
export interface LoadClock {
  schedule: (callback: () => void, milliseconds: number) => unknown;
  cancel: (handle: unknown) => void;
}
/** Each style/retry owns a bounded attempt. Old timers and idle after failure
 * cannot report successful loading; only an explicit new attempt clears failure.
 */
export function createMapLoadAttempt(
  publish: (state: MapLoadState) => void,
  clock: LoadClock = {
    schedule: (callback, milliseconds) => setTimeout(callback, milliseconds),
    cancel: (handle) => clearTimeout(handle as ReturnType<typeof setTimeout>),
  },
) {
  let generation = 0,
    timer: unknown = null,
    state: MapLoadState = 'MAP_LOADING',
    disposed = false;
  const clear = () => {
    if (timer !== null) clock.cancel(timer);
    timer = null;
  };
  const fail = (token: number, failure: MapFailure) => {
    if (
      disposed ||
      token !== generation ||
      (state !== 'MAP_LOADING' && state !== 'MAP_BASEMAP_REAL')
    )
      return;
    clear();
    state = failure;
    publish(state);
  };
  return {
    begin() {
      clear();
      generation++;
      state = 'MAP_LOADING';
      const token = generation;
      if (!disposed) {
        publish(state);
        timer = clock.schedule(() => fail(token, 'MAP_OFFLINE'), 20000);
      }
      return token;
    },
    complete(
      token: number,
      styleLoaded: boolean,
      tilesLoaded: boolean,
      hasProviderSource: boolean,
    ) {
      if (
        disposed ||
        token !== generation ||
        state !== 'MAP_LOADING' ||
        !styleLoaded ||
        !tilesLoaded ||
        !hasProviderSource
      )
        return;
      clear();
      state = 'MAP_BASEMAP_REAL';
      publish(state);
    },
    fail,
    dispose() {
      disposed = true;
      generation++;
      clear();
    },
  };
}
